import { useState } from "react"
import { CalendarContext } from "../context"
import { useFilter } from "../../../hooks/filter"
import { FilterProps } from "../../../hooks/filter/typing"
import { removeAllReference } from "../../../utils/methods/removedReference"
import { Item } from "../../../utils/types/generals"
import { formatFilter } from "../pages/main/constants/filter"
import { useGetData } from "../pages/main/hooks/getData"
import { DownloadType } from "../../../hooks/download/useDownload"
import { ScheduleService } from "../../../service/schedule"
import { httpFetch } from "../../../utils/crud"

const CalendarProvider = (props: { children: JSX.Element }) => {
    const [filterPayload, setFilterPayload] = useState<FilterProps>({ formatFilter: {} })
    const filterData = useFilter(filterPayload)
    const scheduleData = useGetData()
    const [idQuestionnaire, setIdQuestionnaire] = useState<number | null>(null)
    const externalValidation: FilterProps['externalValidation'] = ({ name, value, body, setFilterData }) => {
        let copyBody: Record<string, any> = removeAllReference(body)

        let currentWeek = name == 'filterByCurrentWeek' ? value : !!body.filterByCurrentWeek
        let openScheduled = name == 'open_scheduled' ? value : !!body.open_scheduled
        let programmed = name == 'programmed' ? value : !!body.programmed
        if (copyBody?.keyCodeId) {
            copyBody = {
                ...copyBody,
                keyCodeId: (copyBody?.keyCodeId as Item[])?.map((item) => ({
                    ...item,
                    value: item.keyCode,
                })),
            }
        }
        if (name === 'zoneId') {
            const newChildZone = copyBody?.childZone?.filter((e: any) => value.find((e2: any) => e2.value == e.father))

            scheduleData.handleItemsByExternal({ name: 'childZone', value: value?.length <= 0 ? [] : scheduleData.childZones?.filter((itemClass) => value?.some((itemZone: any) => itemZone?.value == itemClass?.father)) })

            setFilterData((prev) => ({
                ...prev,
                childZone: newChildZone,
            }))

            copyBody = {
                ...copyBody,
                childZone: newChildZone,
            }
        } else if (name === 'areas') {
            const { areas, departments: departmentsData } = copyBody
            const newDepartments = departmentsData?.filter((e: any) => areas.find((e2: any) => e2.value == e.areaId))
            let arrayChilds: any[] = []

            areas?.forEach((e: any) => {
                const id = e.value
                const childs = scheduleData.departments.filter((child) => child.areaId == id)
                arrayChilds.push(...childs)
            })

            scheduleData.handleItemsByExternal({ name: 'departments', value: arrayChilds })
            setFilterData((prev) => ({
                ...prev,
                departments: newDepartments,
            }))

            copyBody = {
                ...copyBody,
                departments: newDepartments,
            }
        } else if (name === 'percentage') {
            if (parseInt(value[0]) === 0 && parseInt(value[1]) === 100) {
                delete copyBody.percentage
            }
        } else if (['filterByCurrentWeek', 'open_scheduled', 'programmed'].includes(name)) {
            scheduleData.disabledInputs({ inputs: ['createYear', 'month', 'from', 'to'], value: !!currentWeek || (!!openScheduled && !programmed) })
        }

        if (name == 'programmed' && programmed && openScheduled) {
            copyBody.open_scheduled = false
            setFilterData((prev) => ({
                ...prev,
                open_scheduled: false,
            }))
        }
        if (name == 'open_scheduled' && programmed && openScheduled) {
            scheduleData.disabledInputs({ inputs: ['createYear', 'month', 'from', 'to'], value: true })
            copyBody.programmed = false
            setFilterData((prev) => ({
                ...prev,
                programmed: false,
            }))
        }
        if (!!currentWeek || !!openScheduled) {
            setFilterData(prev => ({
                ...prev,
                createYear: null,
                month: null,
                from: null,
                to: null,
            }))
            delete copyBody.createYear
            delete copyBody.month
            delete copyBody.from
            delete copyBody.to
        }
        return copyBody
    }
    const filterSchedule = useFilter({
        externalValidation,
        formatFilter,
        customeItemQuearyValue: {
            name: ['open_scheduled', 'programmed'],
            transfrom: ({ name, value, body }) => {
                if (name == 'programmed' && (body.open_scheduled === undefined || body.open_scheduled === null)) {
                    return value ? `open_scheduled=false` : ''
                }
                if (name == 'open_scheduled') {
                    if (body.programmed && body.open_scheduled) return ''
                    if (body.programmed) return `open_scheduled=false`
                    if (body.open_scheduled) return `open_scheduled=true`
                    return ``
                }
                return `${name}=${String(value)}`
            }
        }
    })
    const [isLoading, setIsLoading] = useState(false)
    const [typeDownload, setTypeDownload] = useState<DownloadType | undefined>()
    const [uuid, setUuid] = useState('')
    const getDetailInform = async ({ query, type }: { query?: string, type?: DownloadType }) => {
        if (!uuid) {
            const response = await ScheduleService(httpFetch).getRegister({ type: type as DownloadType, query });
            if (response.uuid) {
                setUuid(response.uuid)
                setTypeDownload(type)
                setIsLoading(true)
            }
        } else {
            const response = await ScheduleService(httpFetch).generateSummary(uuid);
            if (response.reportUrl) {
                setUuid('')
                fetch(response.reportUrl)
                    .then(res => res.blob())
                    .then(blob => {
                        let extension = '';

                        switch (typeDownload) {
                            case 'pdf':
                                extension = '.pdf';
                                break;
                            case 'excel':
                                extension = '.xlsx';
                                break;
                            case 'word':
                                extension = '.docx';
                                break;
                        }
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', `Agenda por unidad${extension}`);
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);

                    })
                    .catch(err => console.error('Error al descargar el archivo:', err))
                    .finally(() => {
                        setIsLoading(false);
                        //window.open(response?.reportUrl ?? '');
                        setTypeDownload(undefined)
                    });
            }
        }
    }
    return (
        <CalendarContext.Provider
            value={{
                filterData,
                scheduleData,
                filterSchedule,
                externalValidation,
                setFilterPayload,
                idQuestionnaire,
                setIdQuestionnaire,
                getDetailInform,
                isLoading,
                uuid,
                typeDownload
            }}
        >
            {props.children}
        </CalendarContext.Provider>
    )
}

export { CalendarProvider }